import { useMemo } from 'react'
import dayjs from 'dayjs'
import type { RecMatchRow } from '../../../../../types/api/v1'
import type { SupplierMetrics, TerminalMetrics } from '../types/supplier'

function useSupplierMetrics(matches: RecMatchRow[]): SupplierMetrics[] {
  return useMemo(() => {
    if (!matches?.length) return []

    const supplierGroups = new Map<string, RecMatchRow[]>()

    // Group matches by supplier_id (exclude voided matches)
    matches.forEach((match) => {
      // Skip voided matches entirely
      if (match.status === 'void') {
        return
      }

      const supplierId = match.supplier_id
      if (!supplierId) return

      if (!supplierGroups.has(supplierId)) {
        supplierGroups.set(supplierId, [])
      }
      supplierGroups.get(supplierId)!.push(match)
    })

    // Transform each supplier group into SupplierMetrics
    return Array.from(supplierGroups.entries()).map(([supplierId, supplierMatches]) => {
      const firstMatch = supplierMatches[0]

      // Count invoices and BOLs - CORRECTED structure
      let totalInvoices = 0
      let totalBOLs = 0
      let pastDueAmount = 0
      let matchedRecordsCount = 0

      supplierMatches.forEach((match) => {
        // CORRECTED: invoice_numbers is util__NumberId[], not string[]
        totalInvoices += match.invoice_numbers?.length || 0
        totalBOLs += match.bol_numbers?.filter((bol) => bol.id !== null && bol.id !== undefined).length || 0

        // Count as matched if it has both invoice IDs and BOL IDs AND has matched status
        const hasInvoiceIds = match.invoice_numbers?.some((inv) => inv.id !== null && inv.id !== undefined) || false
        const hasBolIds = match.bol_numbers?.some((bol) => bol.id !== null && bol.id !== undefined) || false
        const hasMatchedStatus = match.status === 'unapproved' || match.status === 'approved' || match.status === 'hold'
        if (hasInvoiceIds && hasBolIds && hasMatchedStatus) {
          matchedRecordsCount++
        }

        // Past due calculation using API field
        if (match.invoice_due_date && match.invoice_total) {
          const dueDate = dayjs(match.invoice_due_date)
          const daysPastDue = dayjs().diff(dueDate, 'day')
          if (daysPastDue >= 0) {
            pastDueAmount += match.invoice_total
          }
        }
      })

      // Build terminal breakdown from terminals array
      const terminalBreakdown = buildTerminalBreakdown(supplierMatches)

      // Build status counts
      const statusCounts = buildStatusCounts(supplierMatches)

      return {
        supplierId,
        supplierName: firstMatch.supplier_name || 'Unknown',
        buyer: extractBuyerFromAssignee(firstMatch.assignee),
        totalInvoices,
        totalBOLs,
        pastDueAmount,
        statusCounts,
        totalMatches: matchedRecordsCount,
        terminalBreakdown,
      }
    })
  }, [matches])
}

function extractBuyerFromAssignee(assignee?: RecMatchRow['assignee']): string {
  return assignee?.name || assignee?.username || 'Unassigned'
}

function buildTerminalBreakdown(matches: RecMatchRow[]): TerminalMetrics[] {
  const terminalMap = new Map<
    string,
    {
      invoiceCount: number
      bolCount: number
      pastDueAmount: number
    }
  >()

  matches.forEach((match) => {
    // Skip voided matches entirely
    if (match.status === 'void') {
      return
    }

    // Use terminals array from match - this is where terminal data actually is
    const terminals = match.terminals || []
    if (terminals.length === 0) return

    terminals.forEach((terminalName) => {
      if (!terminalMap.has(terminalName)) {
        terminalMap.set(terminalName, { invoiceCount: 0, bolCount: 0, pastDueAmount: 0 })
      }

      const terminalData = terminalMap.get(terminalName)!

      // Distribute match's invoice count across terminals
      const invoiceCount = match.invoice_numbers?.length || 0
      if (invoiceCount > 0) {
        terminalData.invoiceCount += Math.ceil(invoiceCount / terminals.length)
      }

      // Distribute match's BOL count across terminals
      const bolCount = match.bol_numbers?.filter((bol) => bol.id !== null && bol.id !== undefined).length || 0
      if (bolCount > 0) {
        terminalData.bolCount += Math.ceil(bolCount / terminals.length)
      }

      // Distribute past due amount across terminals
      if (match.invoice_due_date && match.invoice_total) {
        const dueDate = dayjs(match.invoice_due_date)
        const daysPastDue = dayjs().diff(dueDate, 'day')
        if (daysPastDue >= 0) {
          terminalData.pastDueAmount += match.invoice_total / terminals.length
        }
      }
    })
  })

  return Array.from(terminalMap.entries()).map(([terminalName, data]) => ({
    terminalId: terminalName,
    terminalName,
    invoiceCount: data.invoiceCount,
    bolCount: data.bolCount,
    pastDueAmount: data.pastDueAmount,
  }))
}

function buildStatusCounts(matches: RecMatchRow[]): Record<string, number> {
  const statusCounts: Record<string, number> = {}

  matches.forEach((match) => {
    // Skip voided matches entirely
    if (match.status === 'void') {
      return
    }

    if (match.status) {
      statusCounts[match.status] = (statusCounts[match.status] || 0) + 1
    }
  })

  return statusCounts
}

export { useSupplierMetrics }
